import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { signIn, resetPassword } from '@fleetops/firebase'
import { AlertCircle } from 'lucide-react'
import { NolensMark, NolensWordmark } from '@fleetops/ui'

const loginSchema = z.object({
  email: z.string().email('Introduza um email válido'),
  password: z.string().min(1, 'A palavra-passe é obrigatória'),
})

type LoginForm = z.infer<typeof loginSchema>

export function LoginPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)

  const { register, handleSubmit, getValues, formState: { errors, isSubmitting } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  async function onSubmit(data: LoginForm) {
    setError(null)
    try {
      await signIn(data.email, data.password)
      navigate('/damages', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao iniciar sessão. Verifique as credenciais.')
    }
  }

  async function handleResetPassword() {
    const email = getValues('email')
    if (!email) {
      setError('Introduza o seu email primeiro.')
      return
    }
    try {
      await resetPassword(email)
      setResetSent(true)
    } catch {
      setError('Não foi possível enviar o email de recuperação. Verifique o endereço.')
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 [&_:focus-visible]:ring-accent-400 [&_:focus-visible]:ring-offset-slate-900">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <NolensMark size={48} />
          <div className="text-center">
            <h1 className="text-xl text-white">
              <NolensWordmark product="fleet" />
            </h1>
            <p className="mt-1.5 text-sm text-slate-400">Gestão operacional de frotas</p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
          <h2 className="mb-6 text-lg font-semibold text-white">Iniciar sessão</h2>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-slate-300">Email</label>
              <input
                {...register('email')}
                id="login-email"
                type="email"
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? 'login-email-error' : undefined}
                autoComplete="email"
                placeholder="utilizador@empresa.com"
                className="h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-slate-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400/30"
              />
              {errors.email && (
                <p id="login-email-error" className="mt-1 text-xs text-red-300">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-slate-300">Palavra-passe</label>
              <input
                {...register('password')}
                id="login-password"
                type="password"
                aria-invalid={errors.password ? true : undefined}
                aria-describedby={errors.password ? 'login-password-error' : undefined}
                autoComplete="current-password"
                placeholder="••••••••"
                className="h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-slate-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400/30"
              />
              {errors.password && (
                <p id="login-password-error" className="mt-1 text-xs text-red-300">{errors.password.message}</p>
              )}
            </div>

            {error && (
              <div role="alert" className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2.5">
                <AlertCircle className="h-4 w-4 shrink-0 text-red-400" aria-hidden />
                <p className="text-xs text-red-300">{error}</p>
              </div>
            )}

            {resetSent && (
              <div role="status" className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2.5">
                <p className="text-xs text-emerald-300">Email de recuperação enviado — verifique a caixa de entrada.</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 flex h-10 w-full items-center justify-center rounded-lg bg-white text-sm font-medium text-slate-900 transition-colors hover:bg-slate-200 disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" role="status" aria-label="A entrar…" />
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          <button
            onClick={handleResetPassword}
            className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-200"
          >
            Esqueceu a palavra-passe?
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Nolens Fleet &copy; {new Date().getFullYear()} &middot; Desenvolvido por rdoria.com
        </p>
      </div>
    </main>
  )
}
